// @flow
import React, {type Node, Fragment} from 'react'
import {type RelayFragmentContainer, createFragmentContainer, graphql} from 'react-relay'
import {uniq} from 'ramda'

import liftNodes from 'utils/GraphQL/typedLiftNodes'
import type {CollectionNode} from 'utils/GraphQL/types'
import Mutator from 'components/proposals/Part/Mutator'

import componentClass from './componentClass'
import type {ChangeList_organization as Organization}
  from './__generated__/ChangeList_organization.graphql'
import type {ChangeList_meeting as Meeting}
  from './__generated__/ChangeList_meeting.graphql'
import type {ChangeList_proposal as Proposal}
  from './__generated__/ChangeList_proposal.graphql'

export type Change = CollectionNode<$PropertyType<Proposal, 'changes'>>

type Props = $ReadOnly<{
  isReadonly: boolean,
  hideAlerts: boolean,
  meeting: ?Meeting,
  organization: Organization,
  proposal: Proposal,
}>

function ChangeList({isReadonly, hideAlerts, meeting, organization, proposal}: Props): Node {
  const changes = liftNodes(proposal.changes).filter(Boolean)
  const showConflictsWarning = proposal.hasConflicts && !hideAlerts

  const changesWithHelpers = uniq(changes).map((change: Change) => [
    change,
    componentClass(change),
    new Mutator(change.id, meeting?.id),
  ])

  return (
    <Fragment>
      {showConflictsWarning && (
        <div className="alert alert-warning" role="alert">
          {I18n.t('proposals.proposal.has_conflicts')}
        </div>
      )}
      {changesWithHelpers.map(([change, ChangeComponent, changeMutator]) => (
        <ChangeComponent
          change={change}
          isReadonly={isReadonly}
          key={change.id}
          mutator={changeMutator}
          organization={organization}
          proposal={proposal}
          showSummary={isReadonly}
        />
      ))}
    </Fragment>
  )
}

ChangeList.defaultProps = {
  meeting: null,
  hideAlerts: false,
}

export default (createFragmentContainer(ChangeList, {
  meeting: graphql`
    fragment ChangeList_meeting on Meeting {
      id
    }
  `,
  proposal: graphql`
    fragment ChangeList_proposal on Proposal {
      id
      hasConflicts
        
      changes(first: 100) @connection(key: "ChangeList_changes") {
        edges {
          node {
            id
            type
            
            ...LinkedRole_change
            ...Role_change
            ...Policy_change
            ...MoveItems_change
            ...ElectRoleFiller_change
          }
        }
      }

      ...Policy_proposal
      ...Role_proposal
    }
  `,
  organization: graphql`
    fragment ChangeList_organization on Organization {
      ...Role_organization
      ...LinkedRole_organization
      ...Policy_organization
    }
  `,
}): RelayFragmentContainer<typeof(ChangeList)>)
